import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotificationsSettings, putNotificationsSettings } from '@blazeteams/sdk';
import { useAuthStore } from '@/store/auth-store';
import { requireCurrentSession, sessionRevision } from '@/features/auth/session-scope';
import { LocalizedError } from '@/lib/i18n/errors';
import { NOTIFICATION_FLAGS, EMAIL_FREQUENCIES, notificationSettingsKey, type NotificationSettings, type NotificationSettingsPatch } from './notification-settings';

function capture(body: NotificationSettingsPatch) {
  return { body: { ...body }, revision: sessionRevision(), account: useAuthStore.getState().user?.id, key: notificationSettingsKey() };
}
function checked(data: unknown, key: string): NotificationSettings {
  const wrapped = data && typeof data === 'object' && 'data' in data;
  const settings = (wrapped ? data.data : data) as NotificationSettings | null;
  if (!settings || NOTIFICATION_FLAGS.some(flag => typeof settings[flag] !== 'boolean') ||
      !EMAIL_FREQUENCIES.some(frequency => frequency === settings.emailFrequency) ||
      (wrapped && 'success' in data && data.success !== true)) throw new LocalizedError('preferences', key);
  return { ...Object.fromEntries(NOTIFICATION_FLAGS.map(flag => [flag, settings[flag]])), emailFrequency: settings.emailFrequency };
}
export const notificationQueries = {
  settings: () => {
    const { account, revision, key } = capture({});
    return { queryKey: key, refetchOnWindowFocus: true, queryFn: async () => {
      requireCurrentSession(revision);
      if (!account) throw new LocalizedError('preferences', 'notifications.loadFailed');
      const { data, error } = await getNotificationsSettings();
      requireCurrentSession(revision);
      if (error) throw new LocalizedError('preferences', 'notifications.loadFailed');
      return checked(data, 'notifications.loadFailed');
    } };
  },
};
export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  const mutation = useMutation({
    scope: { id: JSON.stringify(notificationSettingsKey()) },
    mutationFn: async ({ account, revision, body }: ReturnType<typeof capture>) => {
      requireCurrentSession(revision);
      const keys = Object.keys(body);
      if (!account || !keys.length || keys.some(key => !NOTIFICATION_FLAGS.includes(key as typeof NOTIFICATION_FLAGS[number]) || typeof body[key as keyof NotificationSettingsPatch] !== 'boolean')) {
        throw new LocalizedError('preferences', 'notifications.saveFailed');
      }
      const { data, error } = await putNotificationsSettings({ body });
      requireCurrentSession(revision);
      if (error) throw new LocalizedError('preferences', 'notifications.saveFailed');
      const saved = checked(data, 'notifications.saveFailed');
      if (keys.some(key => saved[key as keyof NotificationSettingsPatch] !== body[key as keyof NotificationSettingsPatch])) throw new LocalizedError('preferences', 'notifications.saveFailed');
      const { data: confirmation, error: readError } = await getNotificationsSettings();
      requireCurrentSession(revision);
      if (readError) throw new LocalizedError('preferences', 'notifications.saveFailed');
      const confirmed = checked(confirmation, 'notifications.saveFailed');
      if (keys.some(key => confirmed[key as keyof NotificationSettingsPatch] !== body[key as keyof NotificationSettingsPatch])) throw new LocalizedError('preferences', 'notifications.saveFailed');
      return confirmed;
    },
    onSuccess: (settings, variables) => {
      if (variables.revision === sessionRevision()) qc.setQueryData(variables.key, settings);
    },
  });
  return {
    ...mutation,
    mutate: (body: NotificationSettingsPatch, options?: Parameters<typeof mutation.mutate>[1]) => mutation.mutate(capture(body), options),
    mutateAsync: (body: NotificationSettingsPatch, options?: Parameters<typeof mutation.mutateAsync>[1]) => mutation.mutateAsync(capture(body), options),
  };
}
